import Link from "next/link";

import { Badge } from "@/components/ui/badge";
import { CandidateAvatar } from "@/components/ui/candidate-avatar";
import { Icon } from "@/components/ui/icon";
import type { BindingOut, EntityRef } from "@/lib/api/model";
import { titleCasePreserveAcronyms } from "@/lib/string/title-case";

import { COPY } from "../../lib/copy";

const PILL = "rounded-sm min-w-0 max-w-full shrink overflow-hidden";

function candidateName(jobCandidate: EntityRef): string {
  return titleCasePreserveAcronyms(jobCandidate.label.trim()) || jobCandidate.label;
}

export function EntityBadge({
  binding,
  job,
  jobCandidate,
  matchNotNeeded = false,
}: {
  binding: BindingOut | null;
  job: EntityRef | null;
  jobCandidate: EntityRef | null;
  /** `match_not_needed`: an unmatched meeting a person marked as internal or personal. */
  matchNotNeeded?: boolean;
}) {
  if (!binding || binding.level === "unresolved") {
    return (
      <span className="text-sm text-contrast/40">
        {matchNotNeeded ? COPY.matchNotNeeded : COPY.unmatched}
      </span>
    );
  }
  const name = jobCandidate ? candidateName(jobCandidate) : "";
  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1">
      {job && (
        <Badge asChild variant="outline-muted" size="sm" className={PILL}>
          <Link
            href={`/jobs/${job.id}/pipeline`}
            // The list row is itself clickable; keep the pipeline link from
            // also firing the row's navigation.
            onClick={(e) => e.stopPropagation()}
          >
            <Icon
              name="IconBag"
              size={12}
              className="shrink-0 text-heading opacity-50"
            />
            <span className="truncate">{job.label}</span>
          </Link>
        </Badge>
      )}
      {jobCandidate && (
        <Badge
          asChild={Boolean(job)}
          variant="outline-muted"
          size="sm"
          className={PILL}
        >
          {job ? (
            <Link
              href={`/jobs/${job.id}/pipeline`}
              onClick={(e) => e.stopPropagation()}
            >
              <CandidateAvatar
                name={name}
                avatarUrl={jobCandidate.avatar_url}
                size={16}
                initialsPx={7}
              />
              <span className="truncate">{name}</span>
            </Link>
          ) : (
            <>
              <CandidateAvatar
                name={name}
                avatarUrl={jobCandidate.avatar_url}
                size={16}
                initialsPx={7}
              />
              <span className="truncate">{name}</span>
            </>
          )}
        </Badge>
      )}
    </span>
  );
}
